import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import {  useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const VendorContext = createContext();
 
export const useVendors = () => {
  const context = useContext(VendorContext);
  if (!context) {
    throw new Error('useVendors must be used within a VendorProvider');
  }
  return context;
};

export const VendorProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError  } = useError();
  const [vendors, setVendors] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/vendors');

  const getVendors = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      let response = await apiInstance.call('vendors?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        response = await apiInstance.call('vendors');  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setVendors(response.data.vendors || []);
       
        
        if(filters){
          return response.data.vendors;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch vendors information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, paginate, getToken]);

  const getVendorByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`vendors/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let vendors = {'data':[response.data.vendor]}
      setVendors(vendors || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/vendors/edit/'+id))){
        if (getToken()) {
        
          
          await getVendorByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getVendorByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getVendors(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getVendors, searchParams, initialized,hideLoading,setApi]);

  const getVendorData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-vendors-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get vendor data'
      });
    } 
  }, [getToken, handleApiError]);

  const addVendor = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();

      // Append documents (files)
      if (data.documents) {
        data.documents.forEach((item, index) => {
          formData.append(`documents[${index}][id]`, item.id);
          formData.append(`documents[${index}][file]`, item.file);
          formData.append(`documents[${index}][name]`, item.name);
          formData.append(`documents[${index}][size]`, item.size);
          formData.append(`documents[${index}][type]`, item.type);
          formData.append(`documents[${index}][created_at]`, item.created_at);
         
        });
      }
      
      // Remove documents from the original object so we can serialize the rest
      const dataCopy = { ...data };
      delete dataCopy.documents;
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('vendors', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the vendors list
        await getVendors();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Vendor'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create vendor'
      });
      return { status: 500, error: error.message };
    }
  };

  const getVendor = (id) => {
    return vendors?.data?.find(vendor => Number(vendor.id) === Number(id));
  };

  const getVendorById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`vendors/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.vendor };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch vendor details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch vendor details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateVendor = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();

      // Append documents (files)
      if (updatedData.documents) {
        updatedData.documents.forEach((item, index) => {
          formData.append(`documents[${index}][id]`, item.id);
          formData.append(`documents[${index}][file]`, item.file);
          formData.append(`documents[${index}][name]`, item.name);
          formData.append(`documents[${index}][size]`, item.size);
          formData.append(`documents[${index}][type]`, item.type);
          formData.append(`documents[${index}][created_at]`, item.created_at);
        });
      }
      
      // Remove documents from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      delete dataCopy.documents;
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      formData.append('_method', 'PUT');

      const response = await apiInstance.call(`vendors/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the vendors list
        await getVendors();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update vendor'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update vendor'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteVendor = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`vendors/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the vendors list
        await getVendors();
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete vendor'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete vendor'
      });
      return { status: 500, error: error.message };
    }
  };

  const value = {
    vendors,
    getVendors,
    getVendorById,
    getVendor,
    addVendor,
    updateVendor,
    deleteVendor,
    getVendorData
  };

  return (
    <VendorContext.Provider value={value}>
      {children}
    </VendorContext.Provider>
  );
};

export default VendorContext; 